import { useState, useCallback } from 'react';
import api from '../../api/axios';

const TRAIT_LABELS = {
  smoking: 'Smoking', drinking: 'Drinking', cannabis: 'Cannabis', diet: 'Diet',
  exercise: 'Exercise', sleep_schedule: 'Sleep Schedule', education: 'Education',
  religion: 'Religion', politics: 'Political Leaning', languages: 'Languages',
  ethnicity: 'Ethnicity', height: 'Height', body_type: 'Body Type',
  has_kids: 'Has Children', wants_kids: 'Wants Children', pets: 'Pets',
  communication_style: 'Communication Style', social_energy: 'Social Energy',
  relationship_goal: 'Relationship Goal', love_language_trait: 'Love Language',
};

const PROMPT_LABELS = {
  simple_pleasures: 'My simple pleasures...',
  geek_out: 'I geek out on...',
  life_goal: 'A life goal of mine...',
  unusual_skill: 'My most unusual skill...',
  green_flags: 'My green flags...',
  perfect_sunday: 'My perfect Sunday...',
  deal_breaker: 'My biggest deal breaker...',
  best_travel: 'Best travel story...',
  dinner_guest: 'Dream dinner guest...',
  hot_take: 'My most controversial opinion...',
  love_language: 'My love language is...',
  pet_peeve: 'My biggest pet peeve...',
  guilty_pleasure: 'My guilty pleasure...',
  two_truths_lie: 'Two truths and a lie...',
  typical_sunday: 'You can find me on a typical Sunday...',
  first_thing_noticed: 'First thing people notice about me...',
  never_do_again: 'Something I will never do again...',
  most_spontaneous: 'Most spontaneous thing I have done...',
  believe_in: 'I believe that...',
  change_mind: 'Something that changed my mind...',
};

const VACCINE_LABELS = {
  covid_19: 'COVID-19', flu: 'Flu', hpv: 'HPV', hepatitis_a: 'Hepatitis A',
  hepatitis_b: 'Hepatitis B', tdap: 'Tdap', mmr: 'MMR', varicella: 'Varicella',
  meningococcal: 'Meningococcal', other: 'Other',
};

export default function ProfileReview({ profile, onSave, onBack }) {
  const [editedProfile, setEditedProfile] = useState(() => ({
    bio: profile.bio || '',
    traits: [...(profile.traits || [])],
    preferences: [...(profile.preferences || [])],
    prompts: [...(profile.prompts || [])],
    vaccinations: [...(profile.vaccinations || [])],
  }));
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const confidence = profile.confidence || {};

  const getConfidenceColor = (key) => {
    const level = confidence[key];
    if (level === 'high') return '#22c55e';
    if (level === 'medium') return '#eab308';
    return '#9CA3AF';
  };

  const getConfidenceDot = (key) => {
    const color = getConfidenceColor(key);
    return (
      <span style={{ ...styles.confidenceDot, backgroundColor: color }}
        title={`Confidence: ${confidence[key] || 'low'}`} />
    );
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const saveEdit = (field) => {
    if (field === 'bio') {
      setEditedProfile((p) => ({ ...p, bio: editValue }));
    } else if (field.startsWith('trait:')) {
      const key = field.split(':')[1];
      setEditedProfile((p) => ({
        ...p,
        traits: p.traits.map((t) =>
          t.trait_key === key ? { ...t, trait_value: editValue } : t
        ),
      }));
    } else if (field.startsWith('prompt:')) {
      const key = field.split(':')[1];
      setEditedProfile((p) => ({
        ...p,
        prompts: p.prompts.map((pr) =>
          pr.prompt_key === key ? { ...pr, response: editValue } : pr
        ),
      }));
    }
    setEditingField(null);
  };

  const removeTrait = (key) => {
    setEditedProfile((p) => ({
      ...p,
      traits: p.traits.filter((t) => t.trait_key !== key),
    }));
  };

  const removePrompt = (key) => {
    setEditedProfile((p) => ({
      ...p,
      prompts: p.prompts.filter((pr) => pr.prompt_key !== key),
    }));
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/onboarding/complete', editedProfile);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }, [editedProfile, onSave]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Your Profile</h2>
        <p style={styles.subtitle}>
          Review what Nova learned about you. Tap any field to edit.
        </p>
        <div style={styles.legendRow}>
          <span style={{ ...styles.legendDot, backgroundColor: '#22c55e' }} /> Explicitly stated
          <span style={{ ...styles.legendDot, backgroundColor: '#eab308', marginLeft: '12px' }} /> Inferred
          <span style={{ ...styles.legendDot, backgroundColor: '#9CA3AF', marginLeft: '12px' }} /> Best guess
        </div>
      </div>

      <div style={styles.scrollArea}>
        {/* Bio */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>About You</h3>
          {editingField === 'bio' ? (
            <div style={styles.editRow}>
              <textarea
                style={styles.editTextarea}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                maxLength={500}
                rows={3}
              />
              <div style={styles.editActions}>
                <button style={styles.saveBtn} onClick={() => saveEdit('bio')}>Save</button>
                <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div
              style={styles.bioText}
              onClick={() => startEditing('bio', editedProfile.bio)}
            >
              {editedProfile.bio || 'No bio generated — tap to add one'}
              {getConfidenceDot('bio')}
            </div>
          )}
        </div>

        {/* Traits */}
        {editedProfile.traits.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Your Traits</h3>
            <div style={styles.chipGrid}>
              {editedProfile.traits.map((t) => (
                <div key={t.trait_key} style={styles.traitChip}>
                  {editingField === `trait:${t.trait_key}` ? (
                    <div style={styles.editRow}>
                      <input
                        style={styles.editInput}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                      <button style={styles.saveBtn} onClick={() => saveEdit(`trait:${t.trait_key}`)}>
                        &#10003;
                      </button>
                      <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>
                        &#10005;
                      </button>
                    </div>
                  ) : (
                    <>
                      <span style={styles.traitLabel}>
                        {TRAIT_LABELS[t.trait_key] || t.trait_key}
                      </span>
                      <span
                        style={styles.traitValue}
                        onClick={() => startEditing(`trait:${t.trait_key}`, t.trait_value)}
                      >
                        {t.trait_value}
                      </span>
                      {getConfidenceDot(t.trait_key)}
                      <button style={styles.removeBtn} onClick={() => removeTrait(t.trait_key)}>
                        &#10005;
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preferences */}
        {editedProfile.preferences.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Partner Preferences</h3>
            <div style={styles.chipGrid}>
              {editedProfile.preferences.map((p) => (
                <div key={p.pref_key} style={styles.traitChip}>
                  <span style={styles.traitLabel}>
                    {TRAIT_LABELS[p.pref_key] || p.pref_key}
                  </span>
                  <span style={styles.traitValue}>{p.pref_value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prompts */}
        {editedProfile.prompts.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Profile Prompts</h3>
            {editedProfile.prompts.map((p) => (
              <div key={p.prompt_key} style={styles.promptCard}>
                <span style={styles.promptLabel}>
                  {PROMPT_LABELS[p.prompt_key] || p.prompt_key}
                </span>
                {editingField === `prompt:${p.prompt_key}` ? (
                  <div style={styles.editRow}>
                    <textarea
                      style={styles.editTextarea}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      maxLength={500}
                      rows={2}
                    />
                    <div style={styles.editActions}>
                      <button style={styles.saveBtn} onClick={() => saveEdit(`prompt:${p.prompt_key}`)}>
                        Save
                      </button>
                      <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    style={styles.promptResponse}
                    onClick={() => startEditing(`prompt:${p.prompt_key}`, p.response)}
                  >
                    {p.response}
                    {getConfidenceDot(p.prompt_key)}
                  </p>
                )}
                <button
                  style={styles.removeBtn}
                  onClick={() => removePrompt(p.prompt_key)}
                >
                  &#10005;
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Vaccinations */}
        {editedProfile.vaccinations.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Vaccination Status</h3>
            <div style={styles.chipGrid}>
              {editedProfile.vaccinations.map((v) => (
                <div key={v.vaccine_type} style={styles.traitChip}>
                  <span style={styles.traitLabel}>
                    {VACCINE_LABELS[v.vaccine_type] || v.vaccine_type}
                  </span>
                  <span style={styles.traitValue}>
                    {v.status.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Actions */}
      <div style={styles.actions}>
        <button style={styles.backBtn} onClick={onBack}>
          Re-do Conversation
        </button>
        <button
          style={styles.confirmBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Looks Good!'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '500px',
    maxHeight: '80vh',
    backgroundColor: '#FAFAFA',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 20px 12px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '4px',
    fontFamily: 'DM Sans, sans-serif',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9CA3AF',
    margin: '0 0 8px',
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '11px',
    color: '#9CA3AF',
    gap: '4px',
  },
  legendDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#E8576D',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
    fontFamily: 'DM Sans, sans-serif',
  },
  bioText: {
    fontSize: '15px',
    color: '#111827',
    lineHeight: '1.6',
    padding: '10px 14px',
    backgroundColor: '#F3F4F6',
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    position: 'relative',
  },
  chipGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  traitChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: '#F3F4F6',
    borderRadius: '16px',
    fontSize: '13px',
    position: 'relative',
  },
  traitLabel: {
    color: '#9CA3AF',
    fontFamily: 'DM Sans, sans-serif',
  },
  traitValue: {
    color: '#111827',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  confidenceDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginLeft: '4px',
    verticalAlign: 'middle',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#D1D5DB',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '0 2px',
    lineHeight: 1,
  },
  promptCard: {
    backgroundColor: '#F3F4F6',
    borderRadius: '10px',
    padding: '12px 14px',
    marginBottom: '8px',
    position: 'relative',
  },
  promptLabel: {
    fontSize: '12px',
    color: '#E8576D',
    fontWeight: '500',
    display: 'block',
    marginBottom: '4px',
    fontFamily: 'DM Sans, sans-serif',
  },
  promptResponse: {
    fontSize: '14px',
    color: '#111827',
    margin: 0,
    lineHeight: '1.5',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  editRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  editInput: {
    flex: 1,
    minWidth: '100px',
    padding: '4px 8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E8576D',
    borderRadius: '6px',
    color: '#111827',
    fontSize: '13px',
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
  },
  editTextarea: {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E8576D',
    borderRadius: '8px',
    color: '#111827',
    fontSize: '14px',
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
    resize: 'vertical',
  },
  editActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '6px',
  },
  saveBtn: {
    background: '#E8576D',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  cancelBtn: {
    background: 'transparent',
    color: '#9CA3AF',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '4px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  errorBanner: {
    padding: '8px 16px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    fontSize: '13px',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
    justifyContent: 'space-between',
  },
  backBtn: {
    background: 'transparent',
    color: '#9CA3AF',
    border: '1px solid #D1D5DB',
    borderRadius: '20px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
  confirmBtn: {
    background: '#E8576D',
    color: '#fff',
    border: 'none',
    borderRadius: '20px',
    padding: '10px 28px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
  },
};
